import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Row, 
  Col 
} from 'antd';
import { useTranslation } from 'react-i18next';
import FilterPanel from '../../components/common/FilterPanel';
import { TaskSearchParams, PlanType, Priority, TaskStatus, Division } from './task';
import hqDisplayGroupService from '../../services/hqDisplayGroupService'; // 导入 StoreDisplayGroupService
import categoryService from '../../services/categoryService'; // 导入 categoryService
import { warehouseService } from '../../services/warehouseService';


const { RangePicker } = DatePicker;
const { Option } = Select;

interface TaskSearchProps {
  onSearch: (params: TaskSearchParams) => void;
  loading?: boolean;
}

const TaskSearch: React.FC<TaskSearchProps> = ({ onSearch, loading = false }) => {
  const { t } = useTranslation();
  const [shopOptions, setShopOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [displayGroupOptions, setDisplayGroupOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [divisionOptions, setDivisionOptions] = useState<Array<{ value: string; label: string }>>([]); // 修改为动态获取

  // 计划类型选项
  const planTypeOptions: PlanType[] = ['Major Plans', 'Regular Plans', 'Minor Plans', 'Urgent Plans', 'Special Plans'];
  
  // 优先级选项
  const priorityOptions: Priority[] = ['High', 'Medium', 'Low'];
  
  // 状态选项
  const statusOptions: TaskStatus[] = ['Not Started', 'In Progress', 'Paused', 'Completed'];


  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      const [shopRes, displayGroupRes, divisionRes] = await Promise.all([
        warehouseService.listWarehouse({}),
           // 添加获取 displayGroupOptions 的逻辑
        hqDisplayGroupService.getDisplayGroups({ status: 'ENABLE' }),
        categoryService.listDivision()
      ]);

      if (shopRes.success && shopRes.data) {
        let options = shopRes.data.map(e => ({ value: e.code, label: e.name }));
        setShopOptions(options || []);
      }

      if (displayGroupRes && displayGroupRes.success && displayGroupRes.data) {
        setDisplayGroupOptions(displayGroupRes.data.map((group: any) => ({
          value: group.code,
          label: group.name,
        })));
      }

      if (divisionRes && divisionRes.success && divisionRes.data) {
        // 根据返回的数据结构，将 value 和 label 映射为 value 和 label
        const options = divisionRes.data.map((item: any) => ({
          value: item.value,
          label: item.label
        }));
        setDivisionOptions(options);
      }
      
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const handleSearch = (values: any) => {
    const searchParams: TaskSearchParams = {
      ...values,
      planStartDate: values.planStartDate ? [
        values.planStartDate[0].valueOf(),
        values.planStartDate[1].valueOf()
      ] : undefined,
      planEndDate: values.planEndDate ? [
        values.planEndDate[0].valueOf(),
        values.planEndDate[1].valueOf()
      ] : undefined,
      generateTime: values.generateTime ? [
        values.generateTime[0].valueOf(),
        values.generateTime[1].valueOf()
      ] : undefined,
      completeTime: values.completeTime ? [
        values.completeTime[0].valueOf(),
        values.completeTime[1].valueOf()
      ] : undefined,
      currentPage: 1,
      pageSize: 20
    };
    onSearch(searchParams);
  };

  const handleReset = () => {
    const resetParams: TaskSearchParams = {
      currentPage: 1,
      pageSize: 20
    };
    onSearch(resetParams);
  };

  const basicFields = (
    <>
      <Col span={6}>
        <Form.Item name="taskCode" label={t('taskManagement.search.taskCode')}>
          <Input placeholder={t('taskManagement.search.placeholders.taskCode')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="shop" label={t('taskManagement.search.shop')}>
          <Select 
            placeholder={t('taskManagement.search.placeholders.shop')} 
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
            // onChange={handleShopChange}
          >
            {shopOptions.map(option => (
              <Option key={option.value} value={option.value} label={option.label}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="status" label={t('taskManagement.search.status')}>
          <Select placeholder={t('taskManagement.search.placeholders.status')} allowClear>
            {statusOptions.map(status => (
              <Option key={status} value={status}>{status}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="division" label={t('taskManagement.search.division')}>
          <Select placeholder={t('taskManagement.search.placeholders.division')} allowClear>
            {divisionOptions.map(division => (
              <Option key={division.label} value={division.value}>{division.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );

  const advancedFields = (
    <>
      <Col span={6}>
        <Form.Item name="displayGroup" label={t('taskManagement.search.displayGroup')}>
          <Select placeholder={t('taskManagement.search.placeholders.displayGroup')} allowClear>
            {displayGroupOptions.map(option => (
              <Option key={option.label} value={option.value}>{option.label}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="shelf" label={t('taskManagement.search.shelf')}>
          <Input placeholder={t('taskManagement.search.placeholders.shelf')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planogramPlan" label={t('taskManagement.search.planogramPlan')}>
          <Input placeholder={t('taskManagement.search.planogramPlan')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planType" label={t('taskManagement.search.planType')}>
          <Select placeholder={t('taskManagement.search.placeholders.planType')} allowClear>
            {planTypeOptions.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="priority" label={t('taskManagement.search.priority')}>
          <Select placeholder={t('taskManagement.search.placeholders.priority')} allowClear>
            {priorityOptions.map(priority => (
              <Option key={priority} value={priority}>{priority}</Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planStartDate" label={t('taskManagement.search.planStartDate')}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="planEndDate" label={t('taskManagement.search.planEndDate')}>
          <RangePicker style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="generateTime" label={t('taskManagement.search.generateTime')}>
          <RangePicker style={{ width: '100%' }} showTime />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="completeTime" label={t('taskManagement.search.completeTime')}>
          <RangePicker style={{ width: '100%' }} showTime />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="hasDelay" label={t('taskManagement.list.columns.isOverdue')}>
          <Select placeholder={t('taskManagement.list.columns.isOverdue')} allowClear>
            <Option value="true">{t('common.yes')}</Option>
            <Option value="false">{t('common.no')}</Option>
          </Select>
        </Form.Item>
      </Col>

    </>
  );

  return (
    <FilterPanel
      onFilter={handleSearch}
      onReset={handleReset}
      initialValues={{}}
      title={t("menu.execution")}
      collapsible={true}
      className="task-search-panel"
      loading={loading}
    >
      {basicFields}
      {advancedFields}
    </FilterPanel>
  );
};

export default TaskSearch;